import { cloneDeep } from "lodash";
import { ASSET_ROWS, LIAB_ROWS } from "@/lib/schema";

const n = (v) => Number(v) || 0;

// green = covered, amber = gap within threshold of need, red = big gap
export const level = (gap, need, amber = 0.25) => (gap <= 0 ? "green" : need > 0 && gap / need <= amber ? "amber" : "red");
export const LEVEL_LABEL = { green: "On track", amber: "Small gap", red: "Action needed", grey: "Not entered", blue: "" };

export const peopleOf = (s) => [["client", s.d.profile.name || "Client"], ...(s.ins.hasSpouse ? [["spouse", s.d.profile.spouse.name || "Spouse"]] : [])];

export const cashFlowLevel = (s) => {
  const cf = s.ov.cashFlow;
  if (cf === null) return "grey";
  return cf >= 0 ? "green" : -cf <= 0.1 * s.ov.takeHomeMonthly ? "amber" : "red";
};

export const retirementLevel = (s) => level(-Math.min(0, s.ret.monthlySurplus), s.ret.monthlyGoal, 0.2);

export function statusCards(s, m) {
  const cf = s.ov.cashFlow;
  const r = s.ret;
  const short = -Math.min(0, r.monthlySurplus);
  const out = [
    {
      key: "cashflow", title: "Monthly cash flow", level: cashFlowLevel(s), value: cf === null ? "Not entered" : `${m(cf)}/mo`,
      text: cf === null ? "Take-home pay has not been entered yet."
        : cf >= 0 ? `${m(cf)} left each month after expenses and debt payments.` : `Spending is ${m(-cf)}/mo more than take-home pay.`,
    },
    {
      key: "retirement", title: `Retirement at ${r.retAge}`, level: retirementLevel(s), value: short ? `${m(-short)}/mo` : "On track",
      text: short ? `Projected ${m(r.expectedMonthly)}/mo vs ${m(r.monthlyGoal)}/mo needed. Saving ${m(r.monthlyContribution)}/mo more closes the gap.`
        : `Projected ${m(r.expectedMonthly)}/mo covers the ${m(r.monthlyGoal)}/mo needed.`,
    },
  ];
  peopleOf(s).forEach(([k, name]) => {
    const x = s.ins[k];
    const haveLife = x.totalNeed - x.lifeAdditional;
    out.push(
      { key: `life-${k}`, title: `Life insurance · ${name}`, level: level(x.lifeAdditional, x.totalNeed), value: x.lifeAdditional > 0 ? m(x.lifeAdditional) : "Covered",
        text: x.lifeAdditional > 0 ? `Needs ${m(x.totalNeed)}; has ${m(haveLife)} in policies and assets.` : `${m(haveLife)} in place covers the ${m(x.totalNeed)} needed.` },
      { key: `di-${k}`, title: `Disability · ${name}`, level: level(x.diShortfall, x.diRequired), value: x.diShortfall > 0 ? `${m(x.diShortfall)}/mo` : "Covered",
        text: x.diShortfall > 0 ? `Needs ${m(x.diRequired)}/mo if unable to work; has ${m(x.diExisting)}/mo.` : `${m(x.diExisting)}/mo in place covers the ${m(x.diRequired)}/mo needed.` },
      { key: `ci-${k}`, title: `Critical illness · ${name}`, level: level(x.ciAdditional, x.ciRequired), value: x.ciAdditional > 0 ? m(x.ciAdditional) : "Covered",
        text: x.ciAdditional > 0 ? `Needs ${m(x.ciRequired)}; has ${m(x.ciExisting)}.` : `${m(x.ciExisting)} in place covers the ${m(x.ciRequired)} needed.` },
    );
  });
  return out;
}

export function headlines(s, m) {
  const gaps = [];
  const lv = [];
  peopleOf(s).forEach(([k, name]) => {
    const x = s.ins[k];
    if (x.lifeAdditional > 0) { gaps.push(`life ${m(x.lifeAdditional)} (${name})`); lv.push(level(x.lifeAdditional, x.totalNeed)); }
    if (x.diShortfall > 0) { gaps.push(`disability ${m(x.diShortfall)}/mo (${name})`); lv.push(level(x.diShortfall, x.diRequired)); }
    if (x.ciAdditional > 0) { gaps.push(`critical illness ${m(x.ciAdditional)} (${name})`); lv.push(level(x.ciAdditional, x.ciRequired)); }
  });
  const protLevel = !gaps.length ? "green" : lv.every((x) => x === "amber") ? "amber" : "red";
  const r = s.ret;
  const ok = r.monthlySurplus >= 0;
  const insPremium = s.plan.rows.filter((x) => x.id !== "retirement").reduce((t, x) => t + x.premium, 0);
  const retRow = s.plan.rows.find((x) => x.id === "retirement");
  const retSave = retRow ? retRow.premium : n(r.monthlyContribution);
  const parts = [
    insPremium ? `insurance premiums ${m(insPremium)}/mo` : gaps.length ? "insurance premiums to be quoted" : null,
    retSave ? `retirement saving ${m(retSave)}/mo` : null,
  ].filter(Boolean);
  return [
    { key: "protected", q: "Is my family protected?", a: !gaps.length ? "Yes" : protLevel === "amber" ? "Partly" : "Not yet", level: protLevel,
      text: gaps.length ? `Gaps to close: ${gaps.join(", ")}.` : "Existing coverage and assets meet the calculated needs." },
    { key: "retire", q: `Can I retire at ${r.retAge}?`, a: ok ? "Yes" : "Not yet", level: retirementLevel(s),
      text: ok ? `Projected income of ${m(r.expectedMonthly)}/mo covers the ${m(r.monthlyGoal)}/mo goal.` : `${m(-r.monthlySurplus)}/mo short. Saving ${m(r.monthlyContribution)}/mo more closes the gap.` },
    { key: "cost", q: "What does it cost per month to fix?", a: insPremium + retSave > 0 ? `${m(insPremium + retSave)}/mo` : gaps.length ? "To be quoted" : `${m(0)}/mo`, level: "blue",
      text: parts.length ? `${parts.join(" + ")}.`.replace(/^./, (c) => c.toUpperCase()) : "No additional cost: the plan is on track." },
  ];
}

export function explain(s, key, m) {
  const { ov, d } = s;
  const r = s.ret;
  const inc = d.financial.income;
  if (key === "cashflow") return {
    title: "Monthly cash flow",
    lines: [["Client take-home pay", n(inc.clientTakeHome)], ["Spouse take-home pay", n(inc.spouseTakeHome)], ["Other take-home income", n(inc.otherTakeHome)],
      ["Monthly expenses (Tab 2)", -s.exp.totalMonthly], ["Debt payments (excl. mortgage)", -ov.debtPayments]],
    total: ["Cash flow / month", ov.cashFlow],
    note: ov.cashFlow === null ? "Enter take-home pay in Tab 2 to calculate cash flow." : "Take-home pay is already after income tax. The mortgage payment is counted once, in the Mortgage / Rent expense line.",
  };
  if (key === "networth") return {
    title: "Net worth",
    lines: [...ASSET_ROWS.map(([k, l]) => [l, n(d.financial.assets[k]?.amount)]), ["Investments (Tab 3)", ov.investmentsTotal],
      ...LIAB_ROWS.map(([k, l]) => [l, -n(d.financial.liabilities[k]?.amount)])].filter(([, v]) => v),
    total: ["Net worth", ov.netWorth], note: "What you own minus what you owe. Insurance policies are not counted.",
  };
  if (key === "retirement") return {
    title: `Retirement income at ${r.retAge}`,
    lines: [...r.sources, ["Projected income", r.expectedMonthly, "sub"], ["Desired monthly income", -r.monthlyGoal]],
    total: [r.monthlySurplus < 0 ? "Monthly shortfall" : "Monthly surplus", r.monthlySurplus],
    note: r.monthlyContribution ? `Closing the gap needs ${m(r.monthlyContribution)}/mo of new savings at ${d.retirement.targetRor}% until 65.` : "Desired income defaults to today's expenses inflated to retirement.",
  };
  if (key === "fin") return {
    title: "Financial Independence Number",
    lines: [["Desired income / year", r.annualGoal], ["Years in retirement", `${r.lifeExp - r.retAge} years`], ["Return after retirement", `${d.retirement.postRetRor}%`], ["Inflation", `${d.assumptions.inflation}%`]],
    total: ["Savings needed at retirement", r.fin], note: "The lump sum that would fund the desired income from retirement to life expectancy.",
  };
  const [kind, who = "client"] = key.split("-");
  const x = s.ins[who];
  const pi = d.insurance[who];
  if (kind === "life") return {
    title: "Life insurance need",
    lines: [["Income replacement", x.incomeRepl ?? 0], ["Family & dependant needs", x.familyNeeds ?? 0], ["Debts marked Insure", x.liabilities], ["Final expenses & obligations", x.obligations],
      ["Total need", x.totalNeed, "sub"], ["Existing life policies", -x.existingLife], ["Assets available at death", -x.availableAssets]],
    total: ["Additional coverage needed", x.lifeAdditional], note: "Never below zero. Each policy and asset is counted once.",
  };
  if (kind === "di") return {
    title: "Disability need (monthly)",
    lines: [[`Income ${m(x.income)}/yr ÷ 12 × ${n(pi.diPct)}%`, x.diRequired], ["Group LTD", -n(pi.groupLtd)], ["Individual disability", -n(pi.individualDi)]],
    total: ["Monthly shortfall", x.diShortfall], note: "Monthly income needed if unable to work, less benefits already in place.",
  };
  return {
    title: "Critical illness need",
    lines: [[`Income × ${n(pi.ciYears)} year(s)`, x.ciRequired], ["Existing critical illness", -x.ciExisting]],
    total: ["Additional protection", x.ciAdditional], note: "A lump sum to cover costs and lost income during recovery.",
  };
}

export const scenarioDefaults = (d) => ({ retAge: n(d.profile.retirementAge) || 65, save: 0, life: 0, ci: 0, di: 0 });

export function applyScenario(d0, sc, accountId = "whatif") {
  const d = cloneDeep(d0);
  d.profile.retirementAge = sc.retAge;
  if (sc.save > 0) {
    d.investments.accounts.push({ id: accountId, type: "Other savings", owner: "Client", value: 0, monthly: sc.save, ror: n(d.retirement.targetRor), purpose: "Retirement", retirement: "Y", use: "N" });
  }
  const c = d.insurance.client;
  c.termLife = n(c.termLife) + sc.life;
  c.criticalIllness = n(c.criticalIllness) + sc.ci;
  c.individualDi = n(c.individualDi) + sc.di;
  return d;
}
